/**
 * WebSocket Context for Real-time Feed Updates
 * Provides live updates for posts, likes, comments, follows
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from './LocationContext';

const WebSocketContext = createContext(null);

// Event types matching backend
export const WSEventType = {
  NEW_POST: 'new_post',
  POST_LIKED: 'post_liked',
  POST_UNLIKED: 'post_unliked',
  POST_COMMENTED: 'post_commented',
  POST_SAVED: 'post_saved',
  POST_DELETED: 'post_deleted',
  USER_FOLLOWED: 'user_followed',
  USER_UNFOLLOWED: 'user_unfollowed',
  NEW_NOTIFICATION: 'new_notification',
};

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const { city } = useLocation();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  
  // Event listeners
  const listenersRef = useRef({});
  
  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const host = baseUrl.replace(/^https?:\/\//, '');
    
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    
    return `${wsProtocol}://${host}/api/ws/feed?${params.toString()}`;
  }, [city]);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    try {
      const url = getWsUrl();
      console.log('[WS] Connecting to:', url);
      
      const ws = new WebSocket(url);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('[WS] Connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Ignore pong messages
          if (message.type === 'pong') return;
          
          console.log('[WS] Message:', message.type);
          setLastMessage(message);
          
          // Dispatch to listeners
          const listeners = listenersRef.current[message.type] || [];
          listeners.forEach(callback => {
            try {
              callback(message.data, message);
            } catch (e) {
              console.error('[WS] Listener error:', e);
            }
          });
          
          // Also dispatch to "all" listeners
          const allListeners = listenersRef.current['*'] || [];
          allListeners.forEach(callback => {
            try {
              callback(message.data, message);
            } catch (e) {
              console.error('[WS] Listener error:', e);
            }
          });
          
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };
      
      ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after 3 seconds
        if (!event.wasClean) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[WS] Attempting reconnect...');
            connect();
          }, 3000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };
      
    } catch (e) {
      console.error('[WS] Connection error:', e);
    }
  }, [getWsUrl]);
  
  // Disconnect
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);
  
  // Send message
  const send = useCallback((type, data = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...data }));
    }
  }, []);
  
  // Subscribe to city updates
  const subscribeToCity = useCallback((newCity) => {
    send('subscribe_city', { city: newCity });
  }, [send]);
  
  // Subscribe to post updates
  const subscribeToPost = useCallback((postId) => {
    send('subscribe_post', { post_id: postId });
  }, [send]);
  
  // Unsubscribe from post updates
  const unsubscribeFromPost = useCallback((postId) => {
    send('unsubscribe_post', { post_id: postId });
  }, [send]);
  
  // Add event listener
  const addListener = useCallback((eventType, callback) => {
    if (!listenersRef.current[eventType]) {
      listenersRef.current[eventType] = [];
    }
    listenersRef.current[eventType].push(callback);
    
    // Return cleanup function
    return () => {
      listenersRef.current[eventType] = listenersRef.current[eventType].filter(
        cb => cb !== callback
      );
    };
  }, []);
  
  // Remove event listener
  const removeListener = useCallback((eventType, callback) => {
    if (listenersRef.current[eventType]) {
      listenersRef.current[eventType] = listenersRef.current[eventType].filter(
        cb => cb !== callback
      );
    }
  }, []);
  
  // Connect on mount, reconnect on auth/city change
  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);
  
  // Update city subscription when city changes
  useEffect(() => {
    if (isConnected && city) {
      subscribeToCity(city);
    }
  }, [city, isConnected, subscribeToCity]);
  
  const value = {
    isConnected,
    lastMessage,
    send,
    subscribeToCity,
    subscribeToPost,
    unsubscribeFromPost,
    addListener,
    removeListener,
    connect,
    disconnect,
    WSEventType,
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

// Hook for subscribing to specific event types
export const useWSEvent = (eventType, callback) => {
  const { addListener } = useWebSocket();
  
  useEffect(() => {
    const cleanup = addListener(eventType, callback);
    return cleanup;
  }, [eventType, callback, addListener]);
};

export default WebSocketContext;
